
import { useCallback, useRef } from "react";
import { QueryClient } from "@tanstack/react-query";

/**
 * Hook for managing prefetching and cache operations related to services
 */
export const useCacheManager = (
  queryClient: QueryClient,
  isLowData: boolean
) => {
  const prefetchedPagesRef = useRef<Set<number>>(new Set());
  
  /**
   * Clear cache and retry services query
   */
  const clearCacheAndRetry = useCallback((
    searchQuery: string, 
    activeSubcategoryId: number | null, 
    selectedRegion: string, 
    page: number, 
    adjustedPageSize: number,
    queryVersion: string
  ) => {
    // Remove all queries related to services
    queryClient.removeQueries({ 
      queryKey: ['services'] 
    });
    
    // Reset the prefetched pages tracking
    prefetchedPagesRef.current = new Set([page]);
    
    // Refetch the current query
    return queryClient.refetchQueries({ 
      queryKey: ['services', searchQuery, activeSubcategoryId, selectedRegion, page, adjustedPageSize, queryVersion],
      exact: true
    });
  }, [queryClient]);

  /**
   * Prefetch next page of results
   */
  const prefetchNextPage = useCallback((
    nextPage: number,
    searchQuery: string, 
    activeSubcategoryId: number | null, 
    selectedRegion: string,
    adjustedPageSize: number,
    queryVersion: string,
    regionData: any,
    prefetchFn: Function
  ) => {
    // Skip if already prefetched or on low data connections
    if (isLowData || prefetchedPagesRef.current.has(nextPage)) return;
    
    // Mark this page as being prefetched
    prefetchedPagesRef.current.add(nextPage);
    
    // Check if already in cache before prefetching
    const queryKey = ['services', searchQuery, activeSubcategoryId, selectedRegion, nextPage, adjustedPageSize, queryVersion];
    const existingData = queryClient.getQueryData(queryKey);
    
    if (existingData) {
      console.log(`Page ${nextPage} already in cache, skipping prefetch`);
      return;
    }
    
    console.log(`Prefetching page ${nextPage}`);
    
    // Prefetch the next page
    queryClient.prefetchQuery({
      queryKey: queryKey,
      queryFn: () => prefetchFn(nextPage, searchQuery, activeSubcategoryId, selectedRegion, adjustedPageSize, regionData),
      staleTime: isLowData ? 60 * 60 * 1000 : 60 * 1000, // 1 hour for low data, 1 minute normal
    });
  }, [queryClient, isLowData]);

  return {
    prefetchNextPage,
    clearCacheAndRetry,
    prefetchedPagesRef
  };
};
